import React, { useCallback, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import ClipLoader from 'react-spinners/ClipLoader'
import { useNavigate } from 'react-router-dom'
import { Icon as IconifyIcon } from '@iconify/react'
import { UserMinus, UserPlus } from 'react-feather'
import moment from 'moment'
import PrivateLayout from '../components/PrivateLayout'
import Footer from '../components/Footer'
import OrganizationSummary from '../components/OrganizationSummary'
import { useAddGrantMutation } from '../store/api/grantApi'
import { CreateGrantRequest, FilterOption, GrantProgram, GranteeType, CreateResearcherData } from '../types'

const grantProgramOptions: FilterOption[] = [
  {
    label: 'Select Area',
    value: '',
  },
  {
    label: GrantProgram.HumilityInTheology,
    value: GrantProgram.HumilityInTheology,
  },
  {
    label: GrantProgram.CharacterVirtueDevelopment,
    value: GrantProgram.CharacterVirtueDevelopment,
  },
  {
    label: GrantProgram.HumanSciences,
    value: GrantProgram.HumanSciences,
  },
  {
    label: GrantProgram.VirtuesAndWellBeing,
    value: GrantProgram.VirtuesAndWellBeing,
  },
]

const granteeTypeOptions: FilterOption[] = [
  {
    label: 'Select Type',
    value: '',
  },
  {
    label: GranteeType.NonProfitOrganization,
    value: GranteeType.NonProfitOrganization,
  },
  {
    label: GranteeType.EducationalInstitution,
    value: GranteeType.EducationalInstitution,
  },
  {
    label: GranteeType.GovernementAgency,
    value: GranteeType.GovernementAgency,
  },
  {
    label: GranteeType.Individual,
    value: GranteeType.Individual,
  },
]

const requestStatusOptions: FilterOption[] = [
  {
    label: 'Select Status',
    value: '',
  },
  {
    label: 'Open',
    value: 'Open',
  },
  {
    label: 'In Progress',
    value: 'In Progress',
  },
  {
    label: 'Cancelled',
    value: 'Cancelled',
  },
  {
    label: 'Closed',
    value: 'Closed',
  },
]

const roleOptions: FilterOption[] = [
  {
    label: 'Select role',
    value: '',
  },
  {
    label: 'Primary Contact',
    value: 'Primary Contact',
  },
  {
    label: 'Project Leader',
    value: 'Project Leader',
  },
  {
    label: 'Project Member',
    value: 'Project Member',
  },
  {
    label: 'Additional',
    value: 'Additional',
  },
]

const defaultResearcherData: CreateResearcherData = {
  firstName: '',
  lastName: '',
  email: '',
  role: '',
}

const AddGrantPage = (): JSX.Element => {
  const navigate = useNavigate()
  const [addGrant] = useAddGrantMutation()

  const [externalId, setExternalId] = useState<string>('')
  const [projectTitle, setProjectTitle] = useState<string>('')
  const [programTheme, setProgramTheme] = useState<string>('')
  const [requestStatus, setRequestStatus] = useState<string>('')
  const [summary, setSummary] = useState<string>('')
  const [organizationName, setOrganizationName] = useState<string>('')
  const [organizationType, setOrganizationType] = useState<string>('')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [agreementAmount, setAgreementAmount] = useState<number>(0)
  const [totalAmount, setTotalAmount] = useState<number>(0)

  const [researcherList, setResearcherList] = useState<CreateResearcherData[]>([
    {
      ...defaultResearcherData,
    },
  ])

  const [isApiRequestInProgress, setIsApiRequestInProgress] = useState<boolean>(false)

  const isFormValid = useMemo(() => {
    return (
      externalId &&
      projectTitle &&
      programTheme &&
      requestStatus &&
      summary &&
      organizationName &&
      organizationType &&
      startDate &&
      endDate &&
      agreementAmount &&
      totalAmount &&
      researcherList.length &&
      researcherList.every((item) => item.firstName && item.lastName && item.email && item.role) &&
      researcherList.some((item) => item.role === 'Primary Contact')
    )
  }, [
    externalId,
    projectTitle,
    programTheme,
    requestStatus,
    summary,
    organizationName,
    organizationType,
    startDate,
    endDate,
    agreementAmount,
    totalAmount,
    researcherList,
  ])

  const onAddGrantClicked = useCallback(async () => {
    if (!isFormValid) {
      return
    }

    const request: CreateGrantRequest = {
      externalId,
      projectTitle,
      programTheme,
      requestStatus,
      summary,
      organizationName,
      organizationType,
      startDate: moment(startDate, 'YYYY-MM-DD').toDate(),
      endDate: moment(endDate, 'YYYY-MM-DD').toDate(),
      agreementAmount,
      totalAmount,
      researchers: researcherList,
    }

    try {
      setIsApiRequestInProgress(true)
      await addGrant({ data: request }).unwrap()

      setIsApiRequestInProgress(false)
      toast('Successfully added')
      navigate('/grants')
    } catch (e) {
      console.error(e)
      toast.error('Failed to add the grant')
      setIsApiRequestInProgress(false)
    }
  }, [
    isFormValid,
    externalId,
    projectTitle,
    programTheme,
    requestStatus,
    summary,
    organizationName,
    organizationType,
    startDate,
    endDate,
    agreementAmount,
    totalAmount,
  ])

  const addNewResearcher = useCallback(() => {
    const newResearcherList: CreateResearcherData[] = [
      ...researcherList,
      {
        ...defaultResearcherData,
      },
    ]

    setResearcherList(newResearcherList)
  }, [researcherList])

  const deleteResearcher = useCallback(
    (index: number) => {
      const newResearcherList: CreateResearcherData[] = researcherList.filter(
        (item: CreateResearcherData, i: number) => i !== index,
      )

      setResearcherList(newResearcherList)
    },
    [researcherList],
  )

  const showPrimaryContactHint = useMemo(() => {
    return !researcherList.some((item) => item.role === 'Primary Contact')
  }, [researcherList])

  const showResearcherHint = useMemo(() => {
    return !researcherList.every((item) => item.firstName && item.lastName && item.email && item.role)
  }, [researcherList])

  const showPrimaryContactAmountHint = useMemo(() => {
    const primaryContacts = researcherList.filter((item) => item.role === 'Primary Contact')

    return primaryContacts.length > 1
  }, [researcherList])

  return (
    <PrivateLayout>
      <div className="content">
        <OrganizationSummary />

        <h3 className="mb-4">Grant</h3>
        <div className="row">
          <div className="col-xl-9">
            <form className="row g-3 mb-6">
              <div className="col-sm-6 col-md-4">
                <div className="form-floating">
                  <input
                    className="form-control"
                    id="externalId"
                    type="text"
                    placeholder="Grant Number"
                    value={externalId}
                    onChange={(e) => {
                      setExternalId(e.target.value)
                    }}
                  />
                  <label htmlFor="externalId">Grant Number</label>
                </div>
              </div>
              <div className="col-sm-6 col-md-8">
                <div className="form-floating">
                  <input
                    className="form-control"
                    id="projectTitle"
                    type="text"
                    placeholder="Title"
                    value={projectTitle}
                    onChange={(e) => {
                      setProjectTitle(e.target.value)
                    }}
                  />
                  <label htmlFor="projectTitle">Project Title</label>
                </div>
              </div>

              <div className="col-12">
                <div className="form-floating">
                  <textarea
                    className="form-control"
                    id="summary"
                    placeholder=""
                    style={{ height: '100px' }}
                    value={summary}
                    onChange={(e) => {
                      setSummary(e.target.value)
                    }}
                  ></textarea>
                  <label htmlFor="summary">Abstract</label>
                </div>
              </div>

              <div className="col-sm-6 col-md-4">
                <div className="form-floating">
                  <select
                    className="form-select"
                    id="organizationType"
                    value={organizationType}
                    onChange={(e) => {
                      setOrganizationType(e.target.value)
                    }}
                  >
                    {granteeTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="organizationType">Grantee Type</label>
                </div>
              </div>

              <div className="col-sm-6 col-md-4">
                <div className="form-floating">
                  <input
                    className="form-control"
                    id="organizationName"
                    type="text"
                    placeholder="Grantee Name"
                    value={organizationName}
                    onChange={(e) => {
                      setOrganizationName(e.target.value)
                    }}
                  />
                  <label htmlFor="organizationName">Grantee Name</label>
                </div>
              </div>

              <div className="col-sm-6 col-md-4">
                <div className="form-floating">
                  <select
                    className="form-select"
                    id="programTheme"
                    value={programTheme}
                    onChange={(e) => {
                      setProgramTheme(e.target.value)
                    }}
                  >
                    {grantProgramOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="programTheme">Grant Area</label>
                </div>
              </div>

              <div className="col-sm-6 col-md-4">
                <div className="flatpickr-input-container">
                  <div className="form-floating">
                    <input
                      className="form-control"
                      id="totalAmount"
                      type="number"
                      placeholder="Project amount"
                      value={totalAmount}
                      onChange={(e) => {
                        setTotalAmount(+e.target.value)
                      }}
                    />
                    <label className="ps-6" htmlFor="totalAmount">
                      Project Amount
                    </label>
                    <IconifyIcon icon="uil:dollar-sign" className="uil uil-dollar-sign flatpickr-icon text-700 me-2" />
                  </div>
                </div>
              </div>

              <div className="col-sm-6 col-md-4">
                <div className="flatpickr-input-container">
                  <div className="form-floating">
                    <input
                      className="form-control"
                      id="agreementAmount"
                      type="number"
                      placeholder="Agreement amount"
                      value={agreementAmount}
                      onChange={(e) => {
                        setAgreementAmount(+e.target.value)
                      }}
                    />
                    <label className="ps-6" htmlFor="agreementAmount">
                      Award Amount
                    </label>
                    <IconifyIcon icon="uil:dollar-sign" className="uil uil-dollar-sign flatpickr-icon text-700 me-2" />
                  </div>
                </div>
              </div>

              <div className="col-sm-6 col-md-4">
                <div className="form-floating">
                  <select
                    className="form-select"
                    id="requestStatus"
                    value={requestStatus}
                    onChange={(e) => {
                      setRequestStatus(e.target.value)
                    }}
                  >
                    {requestStatusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="requestStatus">Status</label>
                </div>
              </div>

              <div className="col-sm-6 col-md-4">
                <div className="flatpickr-input-container">
                  <div className="form-floating">
                    <input
                      className="form-control datetimepicker"
                      id="startDate"
                      type="date"
                      placeholder="project start"
                      data-options='{"disableMobile":true}'
                      value={startDate}
                      onChange={(e) => {
                        setStartDate(e.target.value)
                      }}
                    />
                    <label className="ps-6" htmlFor="startDate">
                      Project Start
                    </label>
                    <IconifyIcon icon="uil:calendar-alt" className="uil uil-dollar-sign flatpickr-icon text-700 me-2" />
                  </div>
                </div>
              </div>

              <div className="col-sm-6 col-md-4">
                <div className="flatpickr-input-container">
                  <div className="form-floating">
                    <input
                      className="form-control datetimepicker"
                      id="endDate"
                      type="date"
                      placeholder="project end"
                      data-options='{"disableMobile":true}'
                      value={endDate}
                      onChange={(e) => {
                        setEndDate(e.target.value)
                      }}
                    />
                    <label className="ps-6" htmlFor="endDate">
                      Project End
                    </label>
                    <IconifyIcon icon="uil:calendar-alt" className="uil uil-dollar-sign flatpickr-icon text-700 me-2" />
                  </div>
                </div>
              </div>

              <div className="row align-items-center justify-content-between g-3 mt-5">
                <div className="col-auto d-flex align-items-center">
                  <h3 className="mb-0">Researcher</h3>
                </div>
                <div className="col-auto bg-white">
                  <button
                    type="button"
                    className="btn btn-primary d-flex"
                    style={{ color: 'white', width: '200px' }}
                    onClick={() => {
                      addNewResearcher()
                    }}
                  >
                    <UserPlus size={16} />
                    <span className="ms-3">Add Grant User</span>
                  </button>
                </div>
              </div>
              <div className="row col-12 pb-6 g-3">
                {researcherList.map((researcher: CreateResearcherData, i: number) => (
                  <>
                    <div className="col-sm-6 col-md-4">
                      <div className="form-floating">
                        <input
                          className="form-control"
                          id="firstName"
                          type="text"
                          placeholder="First Name"
                          value={researcher.firstName}
                          onChange={(e) => {
                            setResearcherList((researcherList) =>
                              researcherList.map((item: CreateResearcherData, index: number) =>
                                index === i
                                  ? {
                                      ...item,
                                      firstName: e.target.value,
                                    }
                                  : item,
                              ),
                            )
                          }}
                        />
                        <label htmlFor="firstName">First Name</label>
                      </div>
                    </div>
                    <div className="col-sm-6 col-md-4">
                      <div className="form-floating">
                        <input
                          className="form-control"
                          id="lastName"
                          type="text"
                          placeholder="Last Name"
                          value={researcher.lastName}
                          onChange={(e) => {
                            setResearcherList((researcherList) =>
                              researcherList.map((item: CreateResearcherData, index: number) =>
                                index === i
                                  ? {
                                      ...item,
                                      lastName: e.target.value,
                                    }
                                  : item,
                              ),
                            )
                          }}
                        />
                        <label htmlFor="lastName">Last Name</label>
                      </div>
                    </div>
                    <div className="col-sm-6 col-md-4">
                      <div className="form-floating">
                        <input
                          className="form-control"
                          id="email"
                          type="email"
                          placeholder="Email"
                          value={researcher.email}
                          onChange={(e) => {
                            setResearcherList((researcherList) =>
                              researcherList.map((item: CreateResearcherData, index: number) =>
                                index === i
                                  ? {
                                      ...item,
                                      email: e.target.value,
                                    }
                                  : item,
                              ),
                            )
                          }}
                        />
                        <label htmlFor="email">Email</label>
                      </div>
                    </div>
                    <div className="col-sm-6 col-md-4">
                      <div className="form-floating">
                        <select
                          className="form-select"
                          id="role"
                          value={researcher.role}
                          onChange={(e) => {
                            setResearcherList((researcherList) =>
                              researcherList.map((item: CreateResearcherData, index: number) =>
                                index === i
                                  ? {
                                      ...item,
                                      role: e.target.value,
                                    }
                                  : item,
                              ),
                            )
                          }}
                        >
                          {roleOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <label htmlFor="role">Role</label>
                      </div>
                    </div>

                    <div className="col-sm-6 col-md-4">
                      {researcherList.length > 1 ? (
                        <button
                          className="btn-link fw-bold fs--1 mt-4 me-3"
                          onClick={() => {
                            deleteResearcher(i)
                          }}
                        >
                          <UserMinus className="text-danger-300" size={16} />
                        </button>
                      ) : null}
                    </div>

                    <hr className="bg-200 mb-4 mt-4" />
                  </>
                ))}
                {showPrimaryContactHint ? (
                  <div className="hint error mt-3">At least one primary contact is required for the grant</div>
                ) : null}
                {showResearcherHint ? (
                  <div className="hint error mt-3">At least 1 researcher is not fully filled</div>
                ) : null}
                {showPrimaryContactAmountHint ? (
                  <div className="hint error mt-3">Maximum 1 primary contact can be added for the grant</div>
                ) : null}
              </div>

              <div className="col-12 gy-6">
                <div className="row g-3 justify-content-end">
                  <div className="col-auto">
                    <button
                      type="button"
                      className="btn btn-phoenix-primary px-5"
                      onClick={() => {
                        navigate('/grants')
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="col-auto">
                    <button
                      type="button"
                      className="btn btn-primary px-5 px-sm-15"
                      disabled={!isFormValid}
                      onClick={() => {
                        onAddGrantClicked()
                      }}
                    >
                      {isApiRequestInProgress ? (
                        <ClipLoader color="white" loading={isApiRequestInProgress} size={15} />
                      ) : (
                        'Create Grant'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        <Footer />
      </div>
    </PrivateLayout>
  )
}

export default AddGrantPage
